import React, { cloneElement, useState } from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { BusinessCollection } from '../../data-layer/business/constants';
import { BusinessModel } from '../../data-layer/business/types';
import { BusinessListItem, BusinessListItemSkeleton } from '../../ui/business/BusinessListItem';
import { Dialog, DialogContent, DialogTitle } from '../../ui/feedback/Dialog';
import { IconClose } from '../../ui/icon/IconClose';
import { IconButton } from '../../ui/input/IconButton';
import { Input } from '../../ui/input/Input';
import { Box } from '../../ui/layout/Box';
import { Flex } from '../../ui/layout/Flex';
import { Stack } from '../../ui/layout/Stack';
import { Typography } from '../../ui/typography/Typography';
import { useTranslation } from '../../utils/useTranslation';
import { useBusinessCallbacks } from '../business/useBusinessCallbacks';
import { useBusinessCollection } from '../business/useBusinessCollection';

interface UserSubscriptionsProps {
    onClose: VoidFunction;
}

export function UserSubscriptions(props: UserSubscriptionsProps) {
    const t = useTranslation();
    const [query, setQuery] = useState('');
    const collection = useBusinessCollection({ namespace: BusinessCollection.SUBSCRIPTIONS });
    const businessCallbacks = useBusinessCallbacks();
    const queryRegex = new RegExp(query, 'i');
    const filteredBusinesses = collection.businesses.filter(
        (business) => queryRegex.test(business.displayName) || queryRegex.test(business.publicId),
    );

    const getBusiness = (business: BusinessModel) =>
        business ? (
            <BusinessListItem business={business} {...businessCallbacks} />
        ) : (
            <BusinessListItemSkeleton />
        );

    useEffectOnce(() => {
        collection.onLoadNext();
    });

    return (
        <Dialog onClose={props.onClose} height={{ xs: '100%', sm: 560 }}>
            <DialogTitle onClose={props.onClose}>
                <Typography variant="title3" weight="bold">
                    {t('Подписки')}
                </Typography>
            </DialogTitle>
            <DialogContent height="100%" py={0} px={0}>
                <Flex
                    px={{ xs: 2, sm: 3 }}
                    py={{ xs: 2, sm: 3 }}
                    direction="column"
                    alignItems="stretch"
                    width="100%"
                    minHeight="100%"
                    background="var(--background-primary)"
                    overflowY="auto"
                >
                    <Box px={0.5} pb={1}>
                        <Input
                            value={query}
                            variant="filled"
                            placeholder="поиск"
                            endAdornment={
                                query ? (
                                    <Box alignSelf="center">
                                        <IconButton
                                            size="xxs"
                                            variant="filled"
                                            onClick={() => setQuery('')}
                                        >
                                            <IconClose />
                                        </IconButton>
                                    </Box>
                                ) : undefined
                            }
                            onChange={(e) => setQuery(e.currentTarget.value)}
                        />
                    </Box>
                    {collection.isLoading || filteredBusinesses.length ? (
                        <Stack
                            width="100%"
                            maxWidth="100%"
                            count={filteredBusinesses.length}
                            renderItem={(index) =>
                                cloneElement(getBusiness(filteredBusinesses[index]), {
                                    key: index,
                                })
                            }
                        />
                    ) : (
                        <Flex alignItems="center" justifyContent="center" flexGrow={1}>
                            <Typography variant="body" weight="medium" color="secondary">
                                Ничего не найдено 👀
                            </Typography>
                        </Flex>
                    )}
                </Flex>
            </DialogContent>
        </Dialog>
    );
}
